import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/**
 * Shared hero-style page header used across all app pages so every page
 * carries the same look: console-style banner with a "meter bridge" trim,
 * icon tile, overline eyebrow, title and subtitle, with an optional actions
 * slot on the right (buttons, chips, filters).
 */
export default function PageHeader({ overline, title, subtitle, icon, actions, sx }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const accent = theme.palette.brand?.accent || '#22d3ee';

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '14px',
        border: '1px solid',
        borderColor: isDark ? 'rgba(148,163,184,0.16)' : 'rgba(15,23,42,0.10)',
        background: isDark
          ? 'linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0) 34%), linear-gradient(120deg, rgba(16,22,34,0.70) 0%, rgba(14,20,30,0.62) 55%, rgba(12,20,30,0.62) 100%)'
          : 'linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.72) 34%), linear-gradient(120deg, rgba(255,255,255,0.92) 0%, rgba(241,245,249,0.88) 55%, rgba(226,232,240,0.82) 100%)',
        backdropFilter: 'blur(16px) saturate(1.3)',
        WebkitBackdropFilter: 'blur(16px) saturate(1.3)',
        boxShadow: isDark
          ? '0 1px 0 rgba(255,255,255,0.05) inset, 0 20px 48px -24px rgba(0,0,0,0.8)'
          : '0 1px 0 rgba(255,255,255,0.8) inset, 0 20px 48px -30px rgba(15,23,42,0.22)',
        px: { xs: 2.5, sm: 3.5 },
        py: { xs: 2.5, sm: 3 },
        mb: 3,
        animation: 'card-rise 420ms ease-out both',
        '@media (prefers-reduced-motion: reduce)': {
          animation: 'none',
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 2,
          background: isDark
            ? 'linear-gradient(90deg, rgba(34,211,238,0.7), rgba(59,130,246,0.18) 42%, transparent 72%)'
            : 'linear-gradient(90deg, rgba(8,145,178,0.5), rgba(59,130,246,0.12) 42%, transparent 72%)',
          boxShadow: isDark ? '0 0 18px rgba(34,211,238,0.45)' : 'none',
          pointerEvents: 'none',
        },
        ...sx,
      }}
    >
      <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap' }}>
        {icon && (
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              color: accent,
              background: 'linear-gradient(145deg, rgba(34,211,238,0.22), rgba(59,130,246,0.12))',
              border: '1px solid rgba(34,211,238,0.38)',
              boxShadow: isDark
                ? '0 14px 34px -12px rgba(34,211,238,0.55), 0 0 28px -6px rgba(34,211,238,0.45)'
                : '0 14px 34px -16px rgba(8,145,178,0.45)',
              '& .MuiSvgIcon-root': {
                fontSize: 29,
                filter: isDark ? 'drop-shadow(0 0 8px rgba(34,211,238,0.55))' : 'none',
              },
            }}
          >
            {icon}
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 220 }}>
          {overline && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
              <Box
                aria-hidden
                sx={{
                  width: 5,
                  height: 5,
                  borderRadius: '1px',
                  bgcolor: accent,
                  boxShadow: isDark ? '0 0 8px rgba(34,211,238,0.7)' : 'none',
                }}
              />
              <Typography variant="overline" sx={{ color: accent, lineHeight: 1.6 }}>
                {overline}
              </Typography>
            </Box>
          )}
          <Typography
            variant="h4"
            sx={{
              color: 'text.primary',
              fontSize: 'clamp(1.55rem, 1.15rem + var(--rs-vw) * 0.013, 2.1rem)',
              textShadow: isDark ? '0 2px 24px rgba(34,211,238,0.12)' : 'none',
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, maxWidth: 720 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {actions && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            {actions}
          </Box>
        )}
      </Box>
    </Box>
  );
}
